import React, { FC } from 'react';
import * as Styled from './TicketSignIn.styles';
import * as O from 'fp-ts/Option';
import { HttpError, HttpStatusCode } from '@core/http';
import { AuthenticateParams, authenticateSchema } from '@modules/auth/model';
import { useTranslations } from '@shared/modules/translation/hooks';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Translations } from '@shared/modules/translation/locales';
import { pipe } from 'fp-ts/function';
import { HttpErrorMessage } from '@shared/components/message/Message';
import { Input, PasswordInput } from '@styles/shared/form/Input';
import FormField from '@shared/modules/form/components/FormField';
import CustomLink from '@shared/components/link/CustomLink';

function getLabelBuilder(error: O.Option<HttpError>, translations: Translations) {
  return pipe(
    error,
    O.fold(
      () => null,
      err => {
        if (err.status === HttpStatusCode.UNAUTHORIZED) return translations.error.message.unauthorized;
        if (err.status === HttpStatusCode.FORBIDDEN) return translations.signIn.forbidden;
        return null;
      },
    ),
  );
}

function getLinkBuilder(error: O.Option<HttpError>, translations: Translations) {
  return pipe(
    error,
    O.fold(
      () => null,
      err => {
        if (err.status === HttpStatusCode.FORBIDDEN)
          return <CustomLink to="contact">{translations.signIn.forbiddenContact}</CustomLink>;
        return null;
      },
    ),
  );
}

interface TicketSignInFormProps {
  loading: boolean;
  error: O.Option<HttpError>;
  onSubmit: (params: AuthenticateParams) => void;
  referrer: string;
}

const TicketSignInForm: FC<TicketSignInFormProps> = ({ loading, error, onSubmit, referrer }) => {
  const translations = useTranslations();

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<AuthenticateParams>({
    resolver: zodResolver(authenticateSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const labelBuilder = getLabelBuilder(error, translations);
  const linkBuilder = getLinkBuilder(error, translations);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Styled.TicketSignInFormContainer>
        <Styled.TicketSignInFormContent>
          <HttpErrorMessage error={error} labelBuilder={labelBuilder}>
            {linkBuilder}
          </HttpErrorMessage>

          <Styled.TicketSignInFormInput label={translations.signIn.email} htmlFor="email" required>
            <Input
              id="email"
              type="email"
              placeholder={translations.signIn.emailPlaceholder}
              error={!!errors.email}
              {...register('email')}
            />
          </Styled.TicketSignInFormInput>

          <FormField label={translations.signIn.password} htmlFor="password" required>
            <PasswordInput
              id="password"
              placeholder={translations.signIn.password}
              error={!!errors.password}
              {...register('password')}
            />
          </FormField>

          <Styled.TicketSignInFormButton type="submit" $loading={loading}>
            {translations.signIn.login}
          </Styled.TicketSignInFormButton>

          <Styled.TicketSignInFormLink to="forgot-password">
            {translations.signIn.forgotPassword}
          </Styled.TicketSignInFormLink>
        </Styled.TicketSignInFormContent>

        <Styled.TicketSignInFormDivider />

        <Styled.TicketSignInFormWithoutAccount>{translations.signIn.newAccount}</Styled.TicketSignInFormWithoutAccount>

        <Styled.TicketSignInFormLink to="sign-up" queries={{ referrer: referrer }}>
          {translations.signIn.newAccountLink}
        </Styled.TicketSignInFormLink>
      </Styled.TicketSignInFormContainer>
    </form>
  );
};

export default TicketSignInForm;
